import { UploadDetailsFormState } from "../redux/types/form.types";

import * as forge from "node-forge";
import CryptoJS from "crypto-js";

import * as utf8 from "utf8";

import {
	DriveFileUpload,
	FileUploadStatus,
} from "@/lib/redux/types/upload.types";

import Pdf from "@/config/type-icons/pdf";
import Doc from "@/config/type-icons/doc";
import Docx from "@/config/type-icons/docx";
import Ppt from "@/config/type-icons/ppt";
import Xls from "@/config/type-icons/xls";
import Xlsx from "@/config/type-icons/xlsx";
import Tif from "@/config/type-icons/tif";
import Tiff from "@/config/type-icons/tiff";
import Pptx from "@/config/type-icons/pptx";
import Jpg from "@/config/type-icons/jpg";
import Jpeg from "@/config/type-icons/jpeg";
import Png from "@/config/type-icons/png";
import Csv from "@/config/type-icons/csv";
import Dwg from "@/config/type-icons/dwg";
import Gif from "@/config/type-icons/gif";
import Rtf from "@/config/type-icons/rtf";
import Txt from "@/config/type-icons/txt";
import Dwf from "@/config/type-icons/dwf";
import Md from "@/config/type-icons/md";
import Msg from "@/config/type-icons/msg";
import Mp3 from "@/config/type-icons/mp3";
import Mp4 from "@/config/type-icons/mp4";
import Mov from "@/config/type-icons/mov";
import EditableFileFormat from "@/config/type-icons/editable-file-format";
import {
	executableExtensions,
	executableMimeTypes,
	FileSize,
} from "@/config/constants";

export const formatBytes = (bytes: number, decimals = 2): string => {
	if (bytes === 0) {
		return "0 Bytes";
	}
	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const calculateOverallProgress = (currentFiles: any[]): number => {
	if (currentFiles.length === 0) {
		return 0;
	}
	const total = currentFiles.reduce((acc, f) => acc + (f.progress || 0), 0);
	return total / currentFiles.length;
};

export function validateEmail(email: string): boolean {
	// Basic sanity check
	if (!email || typeof email !== "string") {
		return false;
	}

	// RFC 5322 compliant regex for email validation
	const emailRegex =
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

	return emailRegex.test(email.trim());
}

export interface ValidationResult {
	isValid: boolean;
	errors: string[];
}

/**
 * Validates UploadDetailsFormState fields and returns errors if any.
 */
export function validateUploadDetails(
	form: UploadDetailsFormState
): ValidationResult {
	const errors: string[] = [];
	const isEmpty = (val: string) => !val || val.trim() === "";

	// 🔹 Check for empty fields
	for (const [key, value] of Object.entries(form)) {
		if (isEmpty(value)) {
			errors.push(`${key} cannot be empty`);
		}
	}

	// 🔹 Email validation
	if (!isEmpty(form.email)) {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(form.email)) {
			errors.push("Invalid email format");
		}
	}

	// 🔹 Phone validation (any country format, basic E.164-friendly check)
	if (!isEmpty(form.phone)) {
		const phoneRegex = /^[+]?[\d\s\-()]{7,20}$/;
		if (!phoneRegex.test(form.phone)) {
			errors.push("Invalid phone number format");
		}
	}

	// 🔹 Date validation
	if (!isEmpty(form.dueDate) && isNaN(Date.parse(form.dueDate))) {
		errors.push("Invalid due date");
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
}

export const getFolderIcon = (type: string) => {
	const fileExtensionArray = type.split(".");
	const extension =
		fileExtensionArray[fileExtensionArray.length - 1]?.toLowerCase();

	const iconMap: any = {
		pdf: Pdf,
		ppt: Ppt,
		pptx: Pptx,
		xls: Xls,
		xlsx: Xlsx,
		doc: Doc,
		docx: Docx,
		tif: Tif,
		tiff: Tiff,
		jpg: Jpg,
		jpeg: Jpeg,
		png: Png,
		csv: Csv,
		dwg: Dwg,
		gif: Gif,
		rtf: Rtf,
		txt: Txt,
		dwf: Dwf,
		md: Md,
		msg: Msg,
		mp3: Mp3,
		mp4: Mp4,
		mov: Mov,
	};

	return iconMap[extension] || EditableFileFormat(extension ?? "Default");
};

export const decryptAES = (text: string, keyy: string) => {
	const block_Cipher = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
	const key = CryptoJS.MD5(utf8.encode(keyy)).toString(CryptoJS.enc.Hex);
	const cipher = forge.cipher.createDecipher(
		"AES-CBC",
		forge.util.createBuffer(key)
	);
	cipher.start({ iv: block_Cipher });
	text = forge.util.decode64(text);
	cipher.update(forge.util.createBuffer(text));
	cipher.finish();

	const decrypted = cipher.output;
	return decrypted.data;
};

export function getUniqueFilename(
	originalName: string,
	existingNames: Set<string>
): string {
	const lastDot = originalName.lastIndexOf(".");
	const base = lastDot === -1 ? originalName : originalName.slice(0, lastDot);
	const ext = lastDot === -1 ? "" : originalName.slice(lastDot);

	let counter = 1;
	let newName = originalName;

	while (existingNames.has(newName)) {
		newName = `${base}_${counter}${ext}`;
		counter++;
	}

	return newName;
}

export const isExecutableFile = (
	fileName: string,
	fileType: string
): boolean => {
	const lastDotIndex = fileName.lastIndexOf(".");
	const fileExtension =
		lastDotIndex > 0 && lastDotIndex < fileName.length - 1
			? fileName.slice(lastDotIndex + 1).toLowerCase()
			: "";

	const normalizedType = fileType.toLowerCase();

	return (
		executableExtensions.includes(fileExtension) ||
		executableMimeTypes.includes(normalizedType)
	);
};

export const getFileUploadProgressReturnValue = (fileSize: number) => {
	if (fileSize === 0 || fileSize <= FileSize.OneMB) {
		return 100;
	} else if (fileSize > FileSize.OneMB && fileSize <= FileSize.TenMB) {
		return 20;
	} else if (fileSize > FileSize.TenMB && fileSize <= FileSize.FiftyMB) {
		return 10;
	} else if (fileSize > FileSize.FiftyMB && fileSize <= FileSize.HundredMB) {
		return 5;
	} else if (
		fileSize > FileSize.HundredMB &&
		fileSize <= FileSize.FiveHundredMB
	) {
		return 2;
	} else {
		return 1;
	}
};

export const calculateUploadProgress = (
	uploadedBytes: number,
	totalBytes: number
): number => {
	return parseInt(((uploadedBytes / totalBytes) * 100).toString(), 10);
};
export const truncateFileName = (name: string, maxLen = 25) => {
	if (!name || name.length <= maxLen) {
		return name;
	}
	const parts = name.split(".");
	const ext = parts.length > 1 ? `.${parts.pop()}` : "";
	const base = parts.join(".");
	const keep = Math.max(6, Math.floor((maxLen - ext.length) / 2));
	const start = base.slice(0, keep);
	const end = base.slice(-keep);
	return `${start}...${end}${ext}`;
};

// for checking seven days otp validation
export const isOtpValidForSevenDays = (
	currentEmail: string,
	verifiedEmail?: string,
	verifiedAt?: string
) => {
	if (!verifiedEmail || !verifiedAt) {
		return false;
	}
	if (currentEmail !== verifiedEmail) {
		return false;
	}

	const lastVerified = new Date(verifiedAt);
	const now = new Date();
	const daysPassed =
		(now.getTime() - lastVerified.getTime()) / (1000 * 60 * 60 * 24);

	return daysPassed <= 7;
};

export const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${(bytes / 1024).toFixed(2)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const getTotalFileSize = (files: DriveFileUpload[]): string => {
	const total = files.reduce(
		(acc, file) =>
			acc + file?.status &&
			file?.status !== FileUploadStatus.UnsupportedFileType
				? file.size
				: 0,
		0
	);
	return formatFileSize(total);
};
